import { Button } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'
import { AuthErrorStateKind } from './auth-error'
import { useAuthErrorState } from './auth-error-store'
import { useLogout } from './use-logout'

// Terminal auth-state gate (ADR-0012). Session-terminal states
// (`inactive`, `not_provisioned`) render an explicit, actionable screen with a
// logout action INSTEAD of the router, so the app never enters a login loop.
// `forbidden` is NOT gated here: the OIDC session is still valid and the user can
// navigate elsewhere, so pages render forbidden inline (role-aware UI is not yet implemented).

interface AuthErrorScreenProps {
	title: string
	message: string
	onLogout: () => void
}

function AuthErrorScreen({ title, message, onLogout }: AuthErrorScreenProps) {
	return (
		<div className="min-h-screen flex items-center justify-center bg-ds-surface">
			<div className="max-w-md w-full text-center space-y-4 p-8">
				<h1 className="text-2xl font-bold text-ds-text">{title}</h1>
				<p className="text-sm text-ds-text-muted">{message}</p>
				<Button variant="outline" onPress={onLogout}>
					Sign out
				</Button>
			</div>
		</div>
	)
}

export function AuthStateGate({ children }: { children: ReactNode }) {
	const errorState = useAuthErrorState()
	const logout = useLogout()

	if (errorState?.kind === AuthErrorStateKind.INACTIVE) {
		return (
			<AuthErrorScreen
				title="Account inactive"
				message="Your account is not active. Please contact an administrator."
				onLogout={logout}
			/>
		)
	}

	if (errorState?.kind === AuthErrorStateKind.NOT_PROVISIONED) {
		return (
			<AuthErrorScreen
				title="Access not set up"
				message="Your sign-in succeeded but no Atlas account is provisioned for you. Please contact an administrator."
				onLogout={logout}
			/>
		)
	}

	return <>{children}</>
}
